import { useState } from 'react';

import type { UploadedDocumentOut } from '@/features/emr/types';
import { EncounterPreviewDialog } from './EncounterPreviewDialog';
import type { EncounterOut } from '@/features/emr/api/encounters-api';
import { UnifiedTimeline } from '@/features/emr/components/UnifiedTimeline';
import type { GeneratedDocumentOut } from '@/features/emr/api/documents-api';
import { type InlineDocumentItem, InlineDocumentViewer } from './InlineDocumentViewer';

interface TimelineTabProps {
  patientId: string;
}

export function TimelineTab({ patientId }: TimelineTabProps) {
  const [selectedDoc, setSelectedDoc] = useState<InlineDocumentItem | null>(null);
  const [previewEncounter, setPreviewEncounter] = useState<EncounterOut | null>(null);

  if (selectedDoc) {
    return <InlineDocumentViewer item={selectedDoc} onBack={() => setSelectedDoc(null)} />;
  }

  return (
    <>
      <UnifiedTimeline
        patientId={patientId}
        showUploadButton={false}
        onOpenEncounter={(enc) => setPreviewEncounter(enc)}
        onOpenUploadedDocument={(doc) => setSelectedDoc(toItemFromUploaded(doc))}
        onOpenGeneratedDocument={(doc) => setSelectedDoc(toItemFromGenerated(doc))}
        onUploadClick={() => {
          // Upload is not offered from the embedded reference panel.
        }}
      />

      <EncounterPreviewDialog
        encounter={previewEncounter}
        onOpenChange={(open) => {
          if (!open) setPreviewEncounter(null);
        }}
      />
    </>
  );
}

function toItemFromGenerated(doc: GeneratedDocumentOut): InlineDocumentItem {
  return {
    id: doc.id,
    type: doc.status,
    source: 'generated',
    content: doc.content,
    name: doc.template?.name ?? 'Generated Document',
    date: { kind: 'instant', value: doc.generated_at },
  };
}

function toItemFromUploaded(doc: UploadedDocumentOut): InlineDocumentItem {
  return {
    id: doc.id,
    name: doc.name,
    source: 'uploaded',
    type: doc.document_type,
    date: { kind: 'calendar', value: doc.document_date },
    content: doc.current_revision?.extracted_text ?? null,
  };
}
